import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { homeForRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import OjentLogo from "@/components/OjentLogo";

const STEPS = ["Profile", "Payments", "First dish", "Floor"];

export default function Onboarding() {
  const { user, loading, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    address: "", phone: user?.restaurant?.phone || "", city: user?.restaurant?.city || "",
    gst_number: "", hours: "11:00 AM – 11:00 PM",
    upi_payload: "",
    category_name: "Starters", item_name: "", item_price: "", item_veg: true,
    table_name: "T1", table_seats: 4, table_section: "Main",
    staff_name: "", staff_email: "", staff_password: "", staff_role: "waiter",
  });

  if (loading || user === null) {
    return <div className="min-h-screen grid place-items-center text-neutral-400">Loading…</div>;
  }
  if (!user) return <Navigate to="/signup" replace />;
  if (user.role !== "admin") return <Navigate to={homeForRole(user.role)} replace />;
  if (user.restaurant?.onboarding_complete) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const finish = async (skipExtras = false) => {
    setError("");
    setBusy(true);
    try {
      const body = skipExtras
        ? { address: form.address, phone: form.phone, city: form.city, hours: form.hours, gst_number: form.gst_number, upi_payload: form.upi_payload }
        : {
          address: form.address, phone: form.phone, city: form.city, hours: form.hours, gst_number: form.gst_number,
          upi_payload: form.upi_payload,
          category_name: form.category_name, item_name: form.item_name, item_price: form.item_price, item_veg: form.item_veg,
          table_name: form.table_name, table_seats: form.table_seats, table_section: form.table_section,
          staff_name: form.staff_name, staff_email: form.staff_email, staff_password: form.staff_password, staff_role: form.staff_role,
        };
      await completeOnboarding(body);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between gap-3 mb-6">
          <OjentLogo markSize={36} showTagline />
          <div className="text-right min-w-0">
            <div className="font-display font-800 text-lg leading-none truncate">{user.restaurant?.name || "Your restaurant"}</div>
            <div className="text-xs text-neutral-500">/{user.restaurant?.slug} · setup wizard</div>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {STEPS.map((s, i) => (
            <button key={s} type="button" onClick={() => setStep(i)}
              className={`flex-1 text-xs py-2 rounded-full ${i === step ? "bg-[var(--kul-primary)] text-white" : "bg-white border text-neutral-500"}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="kul-card p-6 space-y-4" data-testid="onboarding">
          {step === 0 && (
            <>
              <h2 className="font-display text-2xl font-800">Restaurant profile</h2>
              <div><Label>Address</Label><Input value={form.address} onChange={set("address")} placeholder="Shop 12, FC Road" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
                <div><Label>City</Label><Input value={form.city} onChange={set("city")} /></div>
              </div>
              <div><Label>GST number</Label><Input value={form.gst_number} onChange={set("gst_number")} /></div>
              <div><Label>Hours</Label><Input value={form.hours} onChange={set("hours")} /></div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-800">UPI for waiter QR</h2>
              <p className="text-sm text-neutral-500">Paste your UPI ID or Google Business QR text. Waiters will generate the exact bill amount.</p>
              <textarea value={form.upi_payload} onChange={set("upi_payload")} rows={3}
                placeholder="cafe@okaxis  or  upi://pay?pa=cafe@okaxis&pn=Cafe"
                className="w-full rounded-lg border px-3 py-2 text-sm" />
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-display text-2xl font-800">Add your first dish</h2>
              <div><Label>Category</Label><Input value={form.category_name} onChange={set("category_name")} /></div>
              <div><Label>Item name</Label><Input value={form.item_name} onChange={set("item_name")} placeholder="Masala dosa" /></div>
              <div><Label>Price (₹)</Label><Input type="number" value={form.item_price} onChange={set("item_price")} placeholder="80" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.item_veg} onChange={set("item_veg")} /> Vegetarian</label>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-display text-2xl font-800">Floor & first staff</h2>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1"><Label>Table</Label><Input value={form.table_name} onChange={set("table_name")} /></div>
                <div><Label>Seats</Label><Input type="number" value={form.table_seats} onChange={set("table_seats")} /></div>
                <div><Label>Section</Label><Input value={form.table_section} onChange={set("table_section")} /></div>
              </div>
              <p className="text-xs text-neutral-500 pt-2">Optional waiter login (they only see the waiter portal)</p>
              <div><Label>Staff name</Label><Input value={form.staff_name} onChange={set("staff_name")} placeholder="Rahul" /></div>
              <div><Label>Staff email</Label><Input type="email" value={form.staff_email} onChange={set("staff_email")} /></div>
              <div><Label>Staff password</Label><Input type="password" value={form.staff_password} onChange={set("staff_password")} /></div>
            </>
          )}
          {error && <p className="text-sm text-[var(--kul-danger)]">{error}</p>}
          <div className="flex justify-between pt-2">
            <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Button>
            {step < 3 ? (
              <Button type="button" className="bg-[var(--kul-primary)] text-white" onClick={() => setStep(step + 1)}>Next</Button>
            ) : (
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={busy} onClick={() => finish(true)}>Skip extras</Button>
                <Button type="button" disabled={busy} onClick={() => finish(false)} data-testid="onboarding-finish"
                  className="bg-[var(--kul-primary)] text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open restaurant"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
